import React from 'react'
import { ActionPlan, RiskAnalysis, RiskAnalysisStatus, RiskAnalysisType } from '../../../api/schemas/schema'
import { GroupedList } from '../../common/list/GroupedList'
import { IGroupedTableColumnDefintion } from '../../../interfaces/IGroupedTableColumnDefintion'
import { IGroup } from '../../../interfaces/IGroup'
import { ISummaryField } from '../../../interfaces/ISummaryField'
import { Button, makeStyles, MessageBar, MessageBarBody, MessageBarTitle } from '@fluentui/react-components'
import { ArrowExport20Regular } from '@fluentui/react-icons'
import { t } from 'i18next'
import { Card } from '../../common/card/Card'
import { ModalActionPlan } from '../../demandAnalysis/actionPlan/ModalActionPlan'
import { updateActionPlan } from '../../../helpers/riskAnalysisHelper'
import { IItemState } from '../../../interfaces/IItemState'
const useStyles = makeStyles({
   hideInPhone: {
      '@media screen and (max-width: 1007px)': {
         display: 'none',
      },
   },
   wrapText: {
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
      whiteSpace: 'normal',
   },
   exportBtn: {
      float: 'right',
   },
})

type RiskActionPlanProps<T> = {
   title: string
   actionPlans: ActionPlan[]
   analysisState: IItemState<RiskAnalysis>
   setRiskAnalysisState: React.Dispatch<React.SetStateAction<IItemState<RiskAnalysis>>>
   getItemColumns: IGroupedTableColumnDefintion<T>[]
   getItemGroups: IGroup<T>[]
   getItemSummaryFields: ISummaryField[]
   parentRiskInformation: JSX.Element
   selectedItem: ActionPlan
   setSelectedItem: React.Dispatch<React.SetStateAction<ActionPlan>>
   getPhoneBody: (item: T) => JSX.Element
   exportToExcel: (analysis: RiskAnalysis, actionPlans: ActionPlan[]) => void
}

export const RiskActionPlan = <T,>({
   title,
   actionPlans,
   analysisState,
   setRiskAnalysisState,
   getItemColumns,
   getItemGroups,
   getItemSummaryFields,
   parentRiskInformation,
   getPhoneBody,
   selectedItem,
   setSelectedItem,
   exportToExcel,
}: RiskActionPlanProps<T>) => {
   const classes = useStyles()
   const analysis = analysisState.item

   const saveActionPlan = async (data: ActionPlan) => {
      return await updateActionPlan(data, analysis, setRiskAnalysisState)
   }

   const onRowClick = (item: ActionPlan) => {
      setSelectedItem(item)
   }

   return (
      <>
         <Card className={classes.hideInPhone} transparentInPhone title={title}>
            <Button icon={<ArrowExport20Regular />} className={classes.exportBtn} onClick={() => exportToExcel(analysis, actionPlans)} disabled={actionPlans.length === 0}>
               {t('ExportToExcel')}
            </Button>
         </Card>
         {analysis?.actionPlans?.length === 0 ? (
            <MessageBar intent={'info'}>
               <MessageBarBody>
                  <MessageBarTitle className={classes.wrapText}>
                     {(analysis?.simplifiedRisks.length > 0 || analysis?.consequenceAndProbabilityRisks.length > 0) && analysis.actionPlans.length === 0
                        ? t('NoActionPlansRequiredForThisItem', { Item: t('RiskAssesment').toLowerCase() })
                        : t('NoRisksIdentifiedYet')}
                  </MessageBarTitle>
                  {analysis.swoTs.length > 0 && t('RiskAssessmentCanBeCompleted')}
               </MessageBarBody>
            </MessageBar>
         ) : (
            <>
               <GroupedList
                  columns={getItemColumns}
                  groups={getItemGroups}
                  summaryFields={getItemSummaryFields}
                  renderPhoneRow={getPhoneBody}
                  onRowClick={onRowClick}
               />

               <ModalActionPlan
                  parentInformation={selectedItem && parentRiskInformation}
                  selectedItem={selectedItem}
                  close={() => {
                     setSelectedItem(undefined)
                  }}
                  estimatedCost={
                     selectedItem && analysis.type === RiskAnalysisType.SimplifiedRiskAnalysis
                        ? analysis.simplifiedRisks.find((x) => x.id === selectedItem?.riskId)?.estimatedCost
                        : analysis.consequenceAndProbabilityRisks.find((x) => x.id === selectedItem?.riskId)?.estimatedCost
                  }
                  disabled={analysisState.disabled || analysis.status === RiskAnalysisStatus.Completed}
                  onSubmit={saveActionPlan}
               />
            </>
         )}
      </>
   )
}
